import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import api from '../services/api';

const { FiMail, FiLock, FiEye, FiEyeOff, FiCheck, FiInfo, FiAlertTriangle, FiAlertCircle } = FiIcons;

const Register = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { register } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Las contraseñas no coinciden');
      setLoading(false);
      return;
    }
    if (formData.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      setLoading(false);
      return;
    }

    try {
      console.log('Iniciando registro para:', formData.email);
      const data = await register(formData.email, formData.password);
      console.log('Registro exitoso:', data);
      
      // Intentar sincronizar con Acumbamail (no bloqueante)
      if (data.user) {
        try {
          await api.post('/auth/sync-acumbamail', { email: data.user.email });
        } catch (syncError) {
          console.warn('Sincronización con Acumbamail falló (no crítico):', syncError);
        }
      }

      setSuccess(true);
    } catch (error) {
      console.error('Error en registro:', error);
      
      const errorMessage = error.message?.toLowerCase() || '';
      
      if (errorMessage.includes('already registered') || errorMessage.includes('user already exists')) {
        setError('Este correo electrónico ya está registrado. Por favor, inicia sesión.');
      } else if (errorMessage.includes('password') && errorMessage.includes('weak')) {
        setError('La contraseña es demasiado débil. Usa una combinación de letras, números y símbolos.');
      } else if (errorMessage.includes('rate limit')) {
        setError('Has realizado demasiados intentos. Por favor, espera unos minutos.');
      } else if (errorMessage.includes('supabase') && errorMessage.includes('config')) {
        setError('Error de configuración del servidor. Contacta con soporte.');
      } else {
        setError(error.message || 'Ha ocurrido un error inesperado al crear la cuenta. Por favor, inténtalo de nuevo.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  
  const getPasswordStrength = () => {
    const password = formData.password;
    if (password.length === 0) return { strength: 0, label: '' };
    if (password.length < 6) return { strength: 1, label: 'Débil' };
    if (password.length < 8) return { strength: 2, label: 'Media' };
    if (password.length >= 8 && /(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
      return { strength: 4, label: 'Muy fuerte' };
    }
    return { strength: 3, label: 'Fuerte' };
  };

  const passwordStrength = getPasswordStrength();

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="sm:mx-auto sm:w-full sm:max-w-md"
        >
          <div className="bg-white py-8 px-4 shadow-lg sm:rounded-lg sm:px-10 text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <SafeIcon icon={FiCheck} className="h-6 w-6 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">¡Cuenta creada con éxito!</h2>
            <p className="text-gray-600 mb-6">
              Hemos enviado un correo de confirmación a <strong>{formData.email}</strong>.<br/>
              Revisa tu bandeja de entrada (y spam) para activar tu cuenta.
            </p>
            <Link
              to="/login"
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-800 hover:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              Ir a Iniciar Sesión
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <h1 className="text-3xl font-bold text-blue-800 mb-2">BOEResumido.com</h1>
          <h2 className="text-2xl font-bold text-gray-900"> Suscripción Gratuita </h2>
          <p className="mt-2 text-gray-600"> Comienza a recibir tu resumen diario del BOE </p>
        </motion.div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white py-8 px-4 shadow-lg sm:rounded-lg sm:px-10"
        >
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md flex items-start">
                <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 mr-3 flex-shrink-0 mt-0.5" />
                <span className="text-sm">{error}</span>
              </div>
            )}
            
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700"> Correo electrónico </label>
              <div className="mt-1 relative">
                <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input id="email" name="email" type="email" autoComplete="email" required value={formData.email} onChange={handleChange} className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="tu@email.com" />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700"> Contraseña </label>
              <div className="mt-1 relative">
                <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input id="password" name="password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" required value={formData.password} onChange={handleChange} className="pl-10 pr-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="••••••••" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <SafeIcon icon={showPassword ? FiEyeOff : FiEye} className="w-5 h-5" />
                </button>
              </div>
              {formData.password && (
                <div className="mt-2">
                  <div className="flex space-x-1">
                    {[1, 2, 3, 4].map((level) => (
                      <div key={level} className={`h-1 flex-1 rounded ${level <= passwordStrength.strength ? (passwordStrength.strength === 1 ? 'bg-red-500' : passwordStrength.strength === 2 ? 'bg-yellow-500' : passwordStrength.strength === 3 ? 'bg-blue-500' : 'bg-green-500') : 'bg-gray-200'}`} />
                    ))}
                  </div>
                  {passwordStrength.label && (
                    <p className="text-xs text-gray-600 mt-1">{passwordStrength.label}</p>
                  )}
                </div>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700"> Confirmar contraseña </label>
              <div className="mt-1 relative">
                <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input id="confirmPassword" name="confirmPassword" type={showConfirmPassword ? 'text' : 'password'} autoComplete="new-password" required value={formData.confirmPassword} onChange={handleChange} className="pl-10 pr-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="••••••••" />
                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <SafeIcon icon={showConfirmPassword ? FiEyeOff : FiEye} className="w-5 h-5" />
                </button>
              </div>
              {formData.confirmPassword && formData.password === formData.confirmPassword && (
                <div className="mt-1 flex items-center text-green-600">
                  <SafeIcon icon={FiCheck} className="w-4 h-4 mr-1" />
                  <span className="text-xs">Las contraseñas coinciden</span>
                </div>
              )}
            </div>
            
            <div>
              <button type="submit" disabled={loading} className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-800 hover:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creando cuenta...
                  </div>
                ) : (
                  'Crear cuenta gratis'
                )}
              </button>
            </div>
            
            <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded border border-gray-100 text-justify">
               <div className="flex items-start">
                 <SafeIcon icon={FiAlertCircle} className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5 text-gray-400" />
                 <span>Resumen informativo no oficial. Puede contener errores u omisiones. La versión válida es siempre la publicada en el BOE.</span>
               </div>
            </div>
          </form>

          <div className="mt-6">
            <div className="text-center">
              <p className="text-sm text-gray-600"> ¿Ya tienes cuenta?{' '} <Link to="/login" className="font-medium text-blue-800 hover:text-blue-700"> Inicia sesión </Link> </p>
            </div>
          </div>

          <div className="mt-6 text-xs text-center text-gray-400">
            Al registrarte aceptas los <Link to="/terms" className="underline hover:text-gray-600">Términos</Link> y <Link to="/privacy" className="underline hover:text-gray-600">Privacidad</Link>.
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Register;